import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Stars from "@/components/Stars";
import { ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/**
 * Featured reviews across a scope (e.g. category="Repair Services" or all).
 * Shows aggregate rating pill + up to N recent high-rated reviews joined with product name.
 */
export default function FeaturedReviews({ category, limit = 6, minRating = 4, headingKey = "reviews.servicesHeading", subKey = "reviews.servicesSub" }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    params.set("limit", String(limit));
    params.set("min_rating", String(minRating));
    api.get(`/reviews/featured?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [category, limit, minRating]);

  if (loading) return null;
  if (!data) return null;

  const hasReviews = (data.reviews || []).length > 0;
  const basedOn = data.count === 1 ? t("reviews.basedOnOne") : t("reviews.basedOn").replace("{n}", data.count);

  return (
    <section data-testid="featured-reviews-section" className="mt-20">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{t("reviews.customerVoicesBadge")}</div>
          <h2 className="mt-2 font-display font-bold text-3xl sm:text-4xl tracking-tight text-zinc-900">{t(headingKey)}</h2>
          <p className="mt-3 text-zinc-600 max-w-lg">{t(subKey)}</p>
        </div>
        {hasReviews && (
          <div data-testid="reviews-aggregate-pill" className="bg-white rounded-2xl border border-zinc-100 px-5 py-4 flex items-center gap-4 self-start">
            <div className="font-display font-extrabold text-4xl tracking-tighter text-zinc-900" data-testid="reviews-aggregate-avg">
              {data.avg.toFixed(1)}
            </div>
            <div>
              <Stars value={data.avg} size={16} />
              <div className="text-xs text-zinc-500 mt-1" data-testid="reviews-aggregate-count">{basedOn}</div>
            </div>
          </div>
        )}
      </div>

      {!hasReviews ? (
        <div className="bg-white rounded-3xl border border-dashed border-zinc-200 p-10 text-center text-sm text-zinc-500">
          {t("reviews.empty")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.reviews.map((r) => (
            <article key={r.id} data-testid={`featured-review-${r.id}`} className="bg-white rounded-3xl border border-zinc-100 p-6 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Stars value={r.rating} size={14} />
                {r.verified_purchase && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                    <ShieldCheck className="w-3 h-3" /> {t("reviews.verified")}
                  </span>
                )}
              </div>
              <div className="font-display font-bold text-lg text-zinc-900 tracking-tight">{r.title}</div>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed flex-1 line-clamp-4">{r.comment}</p>
              <div className="mt-5 pt-4 border-t border-zinc-100 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-900 truncate">{r.user_name}</div>
                  <div className="text-xs text-zinc-500 truncate">
                    {t("reviews.reviewFor")} <span className="font-medium">{r.product_name}</span>
                  </div>
                </div>
                <Link to={`/product/${r.product_id}#reviews`} className="text-xs font-semibold text-brand hover:underline shrink-0">
                  {t("reviews.readMoreOnProduct")}
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
